/* ══════════════════════════════════════════════════════════════
   Market Data Service — Orchestrates all market data fetches
   Provides polling, data aggregation, and Dhan API integration
   ══════════════════════════════════════════════════════════════ */

import { apiFetch } from './api-client';

// ── Types ─────────────────────────────────────────────────
export interface IndexData {
    name: string;
    symbol: string;
    last: number;
    change: number;
    pChange: number;
    open: number;
    high: number;
    low: number;
}

export interface FIIDIIData {
    latest: {
        date: string;
        fiiNetValue: number;
        diiNetValue: number;
        fiiBuyValue: number;
        fiiSellValue: number;
        diiBuyValue: number;
        diiSellValue: number;
    };
    streak: { fii: number; dii: number };
    fiveDay: { fiiNet: number; diiNet: number };
    thirtyDay?: { fiiNet: number; diiNet: number };
}

export interface MarketBreadthData {
    advances: number;
    declines: number;
    unchanged: number;
    adRatio: number;
    new52wHigh: number;
    new52wLow: number;
    deliveryPercent: number;
    totalTurnover: number;
}

export interface VIXData {
    current: number;
    change: number;
    pChange: number;
}

export interface CommodityData {
    name: string;
    symbol: string;
    price: number;
    change: number;
    pChange: number;
    unit: string;
    high?: number;
    low?: number;
}

export interface CurrencyData {
    pair: string;
    name: string;
    rate: number;
    change: number;
    pChange: number;
    high?: number;
    low?: number;
}

export interface TopMovers {
    gainers: Array<{ symbol: string; lastPrice: number; pChange: number }>;
    losers: Array<{ symbol: string; lastPrice: number; pChange: number }>;
}


// ── Fetch Functions ───────────────────────────────────────

/** Fetch all indices — tries Upstox first, falls back to NSE */
export async function fetchIndices(): Promise<IndexData[]> {
    try {
        // Try Upstox API first (more reliable)
        const result = await apiFetch<{
            indices: Array<{
                name: string; symbol?: string; last: number;
                change: number; pChange: number;
                open: number; high: number; low: number;
            }>;
        }>('/upstox-market', { cacheTTL: 15 });

        if (result.data.indices && result.data.indices.length > 0) {
            return result.data.indices.map(idx => ({
                name: idx.name,
                symbol: idx.symbol || idx.name,
                last: idx.last,
                change: idx.change,
                pChange: idx.pChange,
                open: idx.open,
                high: idx.high,
                low: idx.low,
            }));
        }
    } catch {
        // Upstox failed, try NSE
    }

    try {
        const result = await apiFetch<{ indices: IndexData[] }>('/nse-indices', { cacheTTL: 30 });
        return result.data.indices || [];
    } catch {
        return [];
    }
}

/** Fetch FII/DII flows */
export async function fetchFIIDII(): Promise<FIIDIIData | null> {
    try {
        const result = await apiFetch<FIIDIIData>('/fii-dii', { cacheTTL: 3600 });
        return result.data;
    } catch {
        return null;
    }
}

/** Fetch market breadth */
export async function fetchBreadth(): Promise<MarketBreadthData | null> {
    try {
        const result = await apiFetch<MarketBreadthData>('/market-breadth', { cacheTTL: 30 });
        return result.data;
    } catch {
        return null;
    }
}

/** Fetch India VIX — tries Upstox first, falls back to dedicated endpoint */
export async function fetchVIX(): Promise<VIXData | null> {
    try {
        // Try to get VIX from Upstox market data (it includes VIX index)
        const result = await apiFetch<{
            indices: Array<{ name: string; last: number; change: number; pChange: number }>;
        }>('/upstox-market', { cacheTTL: 15 });

        const vixIdx = result.data.indices?.find(
            i => i.name === 'INDIA VIX' || i.name?.includes('VIX') || i.name?.includes('India VIX')
        );
        if (vixIdx) {
            return {
                current: vixIdx.last,
                change: vixIdx.change,
                pChange: vixIdx.pChange,
            };
        }
    } catch {
        // fall through
    }

    try {
        const result = await apiFetch<VIXData>('/india-vix', { cacheTTL: 30 });
        return result.data;
    } catch {
        return null;
    }
}

/** Fetch MCX commodities */
export async function fetchCommodities(): Promise<CommodityData[]> {
    try {
        const result = await apiFetch<{ commodities: CommodityData[] }>('/mcx-data', { cacheTTL: 60 });
        return result.data.commodities || [];
    } catch {
        return [];
    }
}

/** Fetch INR currency rates */
export async function fetchCurrency(): Promise<CurrencyData[]> {
    try {
        const result = await apiFetch<{ rates: CurrencyData[] }>('/currency', { cacheTTL: 60 });
        return result.data.rates || [];
    } catch {
        return [];
    }
}

/** Fetch top gainers/losers — tries Upstox first */
export async function fetchTopMovers(): Promise<TopMovers | null> {
    try {
        // Try Upstox data (already includes gainers/losers)
        const result = await apiFetch<{
            gainers: Array<{ symbol: string; last: number; pChange: number }>;
            losers: Array<{ symbol: string; last: number; pChange: number }>;
        }>('/upstox-market', { cacheTTL: 15 });

        if (result.data.gainers && result.data.losers) {
            return {
                gainers: result.data.gainers.map(g => ({
                    symbol: g.symbol,
                    lastPrice: g.last,
                    pChange: g.pChange,
                })),
                losers: result.data.losers.map(l => ({
                    symbol: l.symbol,
                    lastPrice: l.last,
                    pChange: l.pChange,
                })),
            };
        }
    } catch {
        // fall through
    }

    try {
        const result = await apiFetch<TopMovers>('/top-gainers-losers', { cacheTTL: 60 });
        return result.data;
    } catch {
        return null;
    }
}

// ── Polling ───────────────────────────────────────────────
type Callback = () => void;
const pollCallbacks: Callback[] = [];
let pollInterval: ReturnType<typeof setInterval> | null = null;

/** Start polling market data (every 30s) */
export function startPolling(callback: Callback, intervalMs: number = 30000): void {
    pollCallbacks.push(callback);

    if (!pollInterval) {
        pollInterval = setInterval(() => {
            pollCallbacks.forEach(cb => cb());
        }, intervalMs);
    }
}

/** Stop all polling */
export function stopPolling(): void {
    if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
    }
    pollCallbacks.length = 0;
}
